<template>
  <header class="irp-header-v2 ph-no-capture dd-privacy-mask">
    <div class="irp-header-v2__record">
      <BittsAvatar v-bind="avatarProps" :show-initials="false" size="large" />
      <div class="irp-header-v2__content">
        <div class="irp-header-v2__name-row">
          <h1 class="irp-header-v2__name">
            {{ individualRecordStore.recordName }}
          </h1>
          <BittsTag
            v-for="population in visiblePopulations"
            :key="population.id"
            :text="population.name"
            color="info"
            size="x-small"
            data-testid="irp-header-population"
          />
          <BittsPopover
            v-if="overflowPopulations.length"
            trigger="hover"
            placement="bottomLeft"
            :mount-to-body="true"
          >
            <span
              class="irp-header-v2__population-overflow"
              data-testid="irp-header-population-overflow"
            >
              +{{ overflowPopulations.length }}
            </span>
            <template #content>
              <div class="irp-header-v2__population-list">
                <BittsTag
                  v-for="population in overflowPopulations"
                  :key="population.id"
                  :text="population.name"
                  color="info"
                  size="x-small"
                />
              </div>
            </template>
          </BittsPopover>
        </div>
        <div class="irp-header-v2__snapshot">
          <BittsLink
            v-if="isValidWebsite"
            :open-in-new-tab="true"
            :text="recordDomain"
            :url="`https://${recordDomain}`"
          />
          <span v-else-if="recordDomain" class="irp-header-v2__domain">
            {{ recordDomain }}
          </span>
          <div v-if="snapshotDetails.length" class="irp-header-v2__details">
            <template v-for="(detail, index) in snapshotDetails" :key="detail">
              <span v-if="index" aria-hidden="true">·</span>
              <span class="irp-header-v2__detail">{{ detail }}</span>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="irp-header-v2__actions">
      <BittsButton
        v-if="recordLinkUrl"
        :href="recordLinkUrl"
        :text="viewInCrmText"
        type="neutral"
        variant="outline"
        target="_blank"
      />
      <component
        :is="shareTooltipOrDiv"
        v-if="canShareWithPartner"
        v-bind="shareTooltipProps"
        @cta-clicked="
          handleBillingInteraction({
            cta: IRP_CTAS.SHARED_LISTS,
            event_site: EVENT_SITES.IRP_CARD_HEADER_TOOLTIP,
          })
        "
      >
        <BittsButton
          text="Share with partner"
          :disabled="!hasListAccess"
          @click="emit('list-modal-visible')"
        />
        <template #title>
          <p> Upgrade to add records to Shared Lists </p>
        </template>
      </component>
    </div>
  </header>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsButton,
  BittsLink,
  BittsPopover,
  BittsTag,
} from '@crossbeam/bitts';
import { EVENT_SITES, IRP_CTAS } from '@crossbeam/itly';

import { computed, watch } from 'vue';

import useBilling from '@/composables/useBilling';
import useRecordHeaderActions from '@/composables/useRecordHeaderActions';
import { MDM_PROPERTIES } from '@/constants/mdm';
import { formatNumberCompact } from '@/date_time_utils';
import { useIndividualRecordStore, usePopulationsStore } from '@/stores';
import type { RecordFieldType } from '@/types/individual_record';
import { REGEX_URL } from '@/utils';

import { HEADER_ARR_FIELD_NAMES } from './irp';

const { sourceId, sourcePrimaryKey } = defineProps<Props>();

const emit = defineEmits<{ 'list-modal-visible': [] }>();

const VISIBLE_POPULATION_COUNT = 4;

type Props = {
  sourceId: number;
  sourcePrimaryKey: string;
};

const individualRecordStore = useIndividualRecordStore();
const populationsStore = usePopulationsStore();
const { handleBillingInteraction } = useBilling();

const ownFields = computed<RecordFieldType[]>(
  () => individualRecordStore.master.own?.fields ?? [],
);

const isCompanyRecord = computed(
  () => individualRecordStore.mdmType !== 'lead',
);

const {
  canShareWithPartner,
  hasListAccess,
  recordLinkUrl,
  shareTooltipOrDiv,
  shareTooltipProps,
  viewInCrmText,
} = useRecordHeaderActions({
  sourceId: () => sourceId,
  sourcePrimaryKey: () => sourcePrimaryKey,
  isCompanyRecord,
});

function fieldByMdmProperty(mdmProperty: string) {
  return ownFields.value.find((field) => field.mdm_property === mdmProperty);
}

function fieldByNames(names: string[]) {
  return ownFields.value.find(
    (field) =>
      names.includes(field.name?.toLowerCase()) ||
      names.includes(field.column?.toLowerCase()),
  );
}

const recordEmail = computed(() =>
  String(fieldByMdmProperty(MDM_PROPERTIES.lead.EMAIL)?.value ?? ''),
);

const isValidWebsite = computed(() =>
  REGEX_URL.test(individualRecordStore.recordWebsite),
);

const recordDomain = computed(() => individualRecordStore.recordDomain);

const avatarProps = computed(() =>
  isCompanyRecord.value
    ? { org: { clearbit_domain: recordDomain.value } }
    : { user: { email: recordEmail.value } },
);

const populations = computed(() =>
  individualRecordStore.ourPopulationIds
    .map(populationsStore.getPopulationById)
    .filter((population) => !!population),
);

const visiblePopulations = computed(() =>
  populations.value.slice(0, VISIBLE_POPULATION_COUNT),
);

const overflowPopulations = computed(() =>
  populations.value.slice(VISIBLE_POPULATION_COUNT),
);

type AccountOwnersType = { items: { fields: RecordFieldType[] }[] };

const ownerName = computed(() => {
  const accountOwners = individualRecordStore.master.own?.account_owners as
    | AccountOwnersType
    | undefined;
  const nameField = accountOwners?.items[0]?.fields.find(
    (field) => field.mdm_property === MDM_PROPERTIES.user.NAME,
  );
  return String(nameField?.value ?? '');
});

const ownOpportunityFields = computed<RecordFieldType[]>(
  () =>
    individualRecordStore.opportunities.own?.opportunities.items[0]?.fields ??
    [],
);

function opportunityFieldByMdmProperty(mdmProperty: string) {
  return ownOpportunityFields.value.find(
    (field) => field.mdm_property === mdmProperty,
  );
}

const revenue = computed(() => {
  const annualRevenue = fieldByNames(HEADER_ARR_FIELD_NAMES)?.value;
  if (typeof annualRevenue === 'number')
    return { label: 'ARR', amount: annualRevenue };
  const opportunityAmount = opportunityFieldByMdmProperty(
    MDM_PROPERTIES.deal.AMOUNT,
  )?.value;
  if (typeof opportunityAmount === 'number')
    return { label: 'Opp amount', amount: opportunityAmount };
  return null;
});

const salesStage = computed(() =>
  String(opportunityFieldByMdmProperty(MDM_PROPERTIES.deal.STAGE)?.value ?? ''),
);

const snapshotDetails = computed(() => {
  const details = [];
  if (ownerName.value) details.push(`Owner: ${ownerName.value}`);
  if (revenue.value)
    details.push(
      `${revenue.value.label}: ${formatNumberCompact(revenue.value.amount)}`,
    );
  if (salesStage.value) details.push(`Stage: ${salesStage.value}`);
  return details;
});

watch(
  () => [sourceId, sourcePrimaryKey] as const,
  () => individualRecordStore.fetchSection('opportunities'),
  { immediate: true },
);
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-header-v2 {
  @apply flex items-start justify-between gap-16;
}

.irp-header-v2__record {
  @apply flex flex-col items-start gap-12 w-2/3 min-w-0 pr-80;
}

.irp-header-v2__content {
  @apply flex flex-col items-start gap-4;
}

.irp-header-v2__name-row {
  @apply flex flex-wrap items-center gap-8;
}

.irp-header-v2__name {
  @apply font-bold text-neutral-text-strong text-xl;
}

.irp-header-v2__snapshot {
  @apply flex flex-wrap items-center gap-x-8 gap-y-4 text-base text-neutral-text;
}

.irp-header-v2__details {
  @apply flex flex-wrap items-center gap-4;
}

.irp-header-v2__detail {
  @apply whitespace-nowrap;
}

.irp-header-v2__actions {
  @apply flex items-center justify-end gap-8 w-1/3;
}

.irp-header-v2__population-overflow {
  @apply shrink-0 cursor-default text-sm text-neutral-text-weak;
}

.irp-header-v2__population-list {
  @apply flex flex-col items-start gap-4 p-8;
}

.irp-header-v2 :deep(.bitts-tag .ant-tag) {
  @apply mr-0;
}
</style>
